// The thin client. Every graph read and write is server-side — the browser
// only ever talks to /api.
async function request(path, options = {}) {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...options,
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.error || `${res.status} ${res.statusText}`)
    err.status = res.status
    err.word = body.word
    throw err
  }
  return body
}

export const api = {
  me: () => request('/api/me'),
  accounts: () => request('/api/accounts'),
  space: (group_id, group_name) => request('/api/space', { method: 'POST', body: JSON.stringify({ group_id, group_name }) }),
  logout: () => request('/auth/disconnect', { method: 'POST' }),
  words: () => request('/api/words'),
  word: id => request(`/api/words/${encodeURIComponent(id)}`),
  create: fields => request('/api/words', { method: 'POST', body: JSON.stringify(fields) }),
  update: (id, fields) => request(`/api/words/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  commit: id => request(`/api/words/${encodeURIComponent(id)}/commit`, { method: 'POST' }),
  // a member's stamp on the node as it stands, and its withdrawal
  stamp: id => request(`/api/words/${encodeURIComponent(id)}/stamp`, { method: 'POST' }),
  unstamp: id => request(`/api/words/${encodeURIComponent(id)}/stamp`, { method: 'DELETE' }),
  remove: id => request(`/api/words/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // A SYNONYM OR AN ANTONYM, BOTH WAYS (owner call 2026-10-04): { kind, id } an
  // existing word, or { kind, term, lexicon?, group? } a free word — linked to
  // the word of that name, else minted as a stub. Answers both words.
  link: (id, fields) => request(`/api/words/${encodeURIComponent(id)}/links`, { method: 'POST', body: JSON.stringify(fields) }),
  // how often every word is used in Ink's notes: { ink: { counts: { [id]: { uses, notes } }, read, sealed } }
  usage: () => request('/api/usage'),
  // nodes a Reference property can point to, by title: Ink's notes, People's people
  refs: q => request(`/api/refs?q=${encodeURIComponent(q)}`),
  unlink: (id, ref) => request(`/api/words/${encodeURIComponent(id)}/unlink`, { method: 'POST', body: JSON.stringify(ref) }),
  // WHAT FRIENDS SHARED WITH YOU, and the two verbs a grant may carry — an
  // edit and a stamp, through the owner's perspective; nothing else
  incoming: () => request('/api/shared/words'),
  updateShared: (owner, id, fields) => request(`/api/shared/${encodeURIComponent(owner)}/words/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  stampShared: (owner, id) => request(`/api/shared/${encodeURIComponent(owner)}/words/${encodeURIComponent(id)}/stamp`, { method: 'POST' }),
  unstampShared: (owner, id) => request(`/api/shared/${encodeURIComponent(owner)}/words/${encodeURIComponent(id)}/stamp`, { method: 'DELETE' }),
  // a word travels between dictionaries as a copy: into your own ('me') or a group's
  copyInto: (into, fields) => request('/api/words/copy', { method: 'POST', body: JSON.stringify({ into, ...fields }) }),
  // many words' tags in one request — the move between dictionaries (lib/move.mjs); every op answers for itself
  retag: ops => request('/api/words/retag', { method: 'POST', body: JSON.stringify({ ops }) }),
  members: group_id => request(`/api/groups/${encodeURIComponent(group_id)}/members`),
  adopt: () => request('/api/adopt', { method: 'POST' }),
  // the dictionaries themselves (lexicon nodes): founded, renamed, made anti, retired
  lexicons: () => request('/api/lexicons'),
  createLexicon: fields => request('/api/lexicons', { method: 'POST', body: JSON.stringify(fields) }),
  updateLexicon: (id, fields) => request(`/api/lexicons/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  absorbSources: () => request('/api/absorb/sources'),
  absorb: source => request('/api/absorb', { method: 'POST', body: JSON.stringify({ source }) }),
  // a dropped document: the bytes, the name in a header; the server reads it
  absorbFile: file => request('/api/absorb/file', { method: 'POST', body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || 'document') } }),
  absorbUrl: url => request('/api/absorb/url', { method: 'POST', body: JSON.stringify({ url }) }),
}
